import { RootState } from "../../app";
import { createSelector } from "@reduxjs/toolkit";
import {
  GroupBlockEntityType,
  GroupBlockEntityTypeWithGroupBlock,
} from "../interfaces/entity";
import { ConfigMap } from "../interfaces/configmap";
import { GroupBlockData } from "../interfaces/groupblock";

type Props = {
  entityId: string;
  index: number;
};

const getConfigMap = (state: RootState) => state.configMapReducer.configMap;
const getEntityId = (_: RootState, props: Props) => props.entityId;
const getIndex = (_: RootState, props: Props) => props.index;

// Cache to maintain referential equality for same inputs
let lastResult: GroupBlockEntityTypeWithGroupBlock | null = null;
let lastEntityId: string | null = null;
let lastIndex: number | null = null;
let lastConfigMapRef: ConfigMap | undefined = undefined;

const entityWithGroupBlockSelector = createSelector(
  [getConfigMap, getEntityId, getIndex],
  (configMap, entityId, index): GroupBlockEntityTypeWithGroupBlock | null => {
    // Return cached result if inputs haven't changed (referential equality check)
    if (
      lastConfigMapRef === configMap &&
      lastEntityId === entityId &&
      lastIndex === index &&
      lastResult !== null
    ) {
      return lastResult;
    }

    if (!configMap?.groupBlocks?.length) {
      lastResult = null;
      lastEntityId = entityId;
      lastIndex = index;
      lastConfigMapRef = configMap;
      return null;
    }

    // Find the entity without creating intermediate objects
    let found:
      | {
          entity: GroupBlockEntityType;
          groupBlock: GroupBlockData;
        }
      | undefined;

    for (const groupBlock of configMap.groupBlocks) {
      const entity = groupBlock.groupBlockEntityTypes.find(
        (e) => e.entityType.id === entityId,
      );
      if (entity) {
        found = { entity, groupBlock };
        break;
      }
    }

    if (!found) {
      lastResult = null;
      lastEntityId = entityId;
      lastIndex = index;
      lastConfigMapRef = configMap;
      return null;
    }

    // Only create new object if index changed or first time
    const result = {
      groupBlock: found.groupBlock,
      entity: {
        ...found.entity,
        index,
      },
    };

    lastResult = result;
    lastEntityId = entityId;
    lastIndex = index;
    lastConfigMapRef = configMap;

    return result;
  },
);

export default entityWithGroupBlockSelector;
